import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { apiClient, type ApiResponse } from '@/lib/api-client';

/**
 * The full list of IANA timezone identifiers supported by the runtime.
 * Uses Intl.supportedValuesOf when available (all modern browsers) so the list
 * stays current automatically; falls back to a small common set otherwise.
 */
function getAllTimezones(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      return intl.supportedValuesOf('timeZone');
    } catch {
      // fall through to fallback
    }
  }
  return [
    'UTC',
    'America/Toronto',
    'America/Winnipeg',
    'America/Edmonton',
    'America/Vancouver',
    'America/Halifax',
    'America/St_Johns',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Dubai',
    'Asia/Karachi',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
  ];
}

/** Current UTC offset label (e.g. "GMT-04:00") for a timezone. */
function tzOffsetLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** Timezone <Select> options, built once. Label shows the zone + UTC offset. */
const TIMEZONE_OPTIONS: { value: string; label: string }[] = getAllTimezones().map((tz) => {
  const offset = tzOffsetLabel(tz);
  const name = tz.replace(/_/g, ' ');
  return { value: tz, label: offset ? `${name} (${offset})` : name };
});

interface GeneralSettingsData {
  firmName: string;
  logo: string | null;
  address: string;
  phone: string;
  email: string;
  website: string;
  businessNumber: string;
  fiscalYearStart: string;
  defaultPaymentTerms: number;
  currencyFormat: string;
  timezone: string;
  businessHoursStart: string;
  businessHoursEnd: string;
  overUtilizedThreshold: number;
  underUtilizedThreshold: number;
  automationDailyRunHour: number;
  invitationLinkValidityHours: number;
}

/**
 * GeneralSettings — firm-level configuration for name, logo, address, fiscal year, etc.
 * Requirements: 15.1, 15.3, 15.8
 */
export function GeneralSettings() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings', 'general'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<GeneralSettingsData>>('/settings/general');
      return response.data.data;
    },
  });

  const [form, setForm] = useState<GeneralSettingsData>({
    firmName: '',
    logo: null,
    address: '',
    phone: '',
    email: '',
    website: '',
    businessNumber: '',
    fiscalYearStart: '01-01',
    defaultPaymentTerms: 30,
    currencyFormat: 'CAD',
    timezone: 'America/Toronto',
    businessHoursStart: '09:00',
    businessHoursEnd: '17:00',
    overUtilizedThreshold: 100,
    underUtilizedThreshold: 60,
    automationDailyRunHour: 8,
    invitationLinkValidityHours: 72,
  });

  // Sync form state when data loads. Fall back to defaults for any keys the
  // tenant hasn't saved yet (e.g. the utilization thresholds on older tenants).
  const [initialized, setInitialized] = useState(false);
  if (settings && !initialized) {
    setForm((prev) => ({
      ...prev,
      ...settings,
      overUtilizedThreshold:
        typeof settings.overUtilizedThreshold === 'number'
          ? settings.overUtilizedThreshold
          : prev.overUtilizedThreshold,
      underUtilizedThreshold:
        typeof settings.underUtilizedThreshold === 'number'
          ? settings.underUtilizedThreshold
          : prev.underUtilizedThreshold,
      automationDailyRunHour:
        typeof settings.automationDailyRunHour === 'number'
          ? settings.automationDailyRunHour
          : prev.automationDailyRunHour,
      invitationLinkValidityHours:
        typeof settings.invitationLinkValidityHours === 'number'
          ? settings.invitationLinkValidityHours
          : prev.invitationLinkValidityHours,
    }));
    setInitialized(true);
  }

  const updateMutation = useMutation({
    mutationFn: async (data: GeneralSettingsData) => {
      const response = await apiClient.put<ApiResponse<GeneralSettingsData>>(
        '/settings/general',
        data,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'general'] });
    },
  });

  const handleChange = (field: keyof GeneralSettingsData, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(form);
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Firm Information */}
      <Card>
        <CardHeader
          title="Firm Information"
          description="Basic details about your firm displayed on invoices and communications."
        />
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Firm Name"
              value={form.firmName}
              onChange={(e) => handleChange('firmName', e.target.value)}
              required
              placeholder="Your Firm Name"
            />
            <Input
              label="Business Number"
              value={form.businessNumber}
              onChange={(e) => handleChange('businessNumber', e.target.value)}
              placeholder="123456789RC0001"
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              placeholder="info@yourfirm.ca"
            />
            <Input
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+1 (416) 555-0100"
            />
            <Input
              label="Website"
              type="url"
              value={form.website}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://yourfirm.ca"
            />
            <div className="sm:col-span-2">
              <Input
                label="Address"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Bay Street, Suite 400, Toronto, ON M5J 2T3"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial Configuration */}
      <Card>
        <CardHeader
          title="Financial Configuration"
          description="Fiscal year, payment terms, currency settings, and financial period controls."
        />
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Fiscal Year Start"
              value={form.fiscalYearStart}
              onChange={(e) => handleChange('fiscalYearStart', e.target.value)}
              options={[
                { value: '01-01', label: 'January 1' },
                { value: '04-01', label: 'April 1' },
                { value: '07-01', label: 'July 1' },
                { value: '10-01', label: 'October 1' },
              ]}
            />
            <Input
              label="Default Payment Terms (days)"
              type="number"
              value={String(form.defaultPaymentTerms)}
              onChange={(e) => handleChange('defaultPaymentTerms', Number(e.target.value))}
              min={1}
              max={365}
            />
            <Select
              label="Currency"
              value={form.currencyFormat}
              onChange={(e) => handleChange('currencyFormat', e.target.value)}
              options={[
                { value: 'CAD', label: 'CAD — Canadian Dollar' },
                { value: 'USD', label: 'USD — US Dollar' },
              ]}
            />
            <Select
              label="Timezone"
              value={form.timezone}
              onChange={(e) => handleChange('timezone', e.target.value)}
              options={TIMEZONE_OPTIONS}
            />
          </div>

          {/* Financial Period Enforcement Toggle */}
          <div className="mt-6 border-t border-border pt-5 dark:border-border-dark">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Financial Period Enforcement
                </div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  When enabled, all financial transactions (invoices, payments, credit notes, etc.)
                  are blocked unless an open financial period exists and the transaction date falls
                  within it. Manage periods from the Financial Periods page.
                </div>
              </div>
              <FinancialPeriodToggle />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Capacity & Workload */}
      <Card>
        <CardHeader
          title="Capacity & Workload"
          description="Utilization thresholds used to flag staff and generate rebalancing suggestions."
        />
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Over-utilized threshold (%)"
              type="number"
              value={String(form.overUtilizedThreshold)}
              onChange={(e) => handleChange('overUtilizedThreshold', Number(e.target.value))}
              min={1}
              max={500}
              hint="Staff above this utilization are flagged as over-utilized (default 100)."
            />
            <Input
              label="Under-utilized threshold (%)"
              type="number"
              value={String(form.underUtilizedThreshold)}
              onChange={(e) => handleChange('underUtilizedThreshold', Number(e.target.value))}
              min={0}
              max={100}
              hint="Staff below this utilization can receive reassigned work (default 60)."
            />
          </div>
        </CardContent>
      </Card>

      {/* User Management */}
      <Card>
        <CardHeader
          title="User Management"
          description="Settings that control staff accounts and invitations."
        />
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Invitation link validity (hours)"
              type="number"
              value={String(form.invitationLinkValidityHours)}
              onChange={(e) => handleChange('invitationLinkValidityHours', Number(e.target.value))}
              min={1}
              max={720}
              hint="How long a new-user invitation / activation link stays valid (default 72, max 720)."
            />
          </div>
        </CardContent>
      </Card>

      {/* Automation */}
      <Card>
        <CardHeader
          title="Automation"
          description="Controls for scheduled automation rules (deadline and date-based triggers)."
        />
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Daily automation run time"
              value={String(form.automationDailyRunHour)}
              onChange={(e) => handleChange('automationDailyRunHour', Number(e.target.value))}
              options={Array.from({ length: 24 }, (_, h) => ({
                value: String(h),
                label: `${String(h).padStart(2, '0')}:00`,
              }))}
            />
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Date and deadline automations (e.g. “5 days after a due date”) are evaluated once a day
            at this hour, in your firm's timezone.
          </p>
        </CardContent>
      </Card>

      {/* Business Hours */}
      <Card>
        <CardHeader
          title="Business Hours"
          description="Used for due date calculations and scheduling."
        />
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Start Time"
              type="time"
              value={form.businessHoursStart}
              onChange={(e) => handleChange('businessHoursStart', e.target.value)}
            />
            <Input
              label="End Time"
              type="time"
              value={form.businessHoursEnd}
              onChange={(e) => handleChange('businessHoursEnd', e.target.value)}
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button type="button" variant="outline" onClick={() => settings && setForm(settings)}>
            Reset
          </Button>
          <Button type="submit" loading={updateMutation.isPending}>
            Save Changes
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
}

/**
 * Self-contained toggle for enabling/disabling financial period enforcement.
 * Reads from billing settings and updates independently of the main form.
 */
function FinancialPeriodToggle() {
  const queryClient = useQueryClient();

  const { data: billingSettings } = useQuery({
    queryKey: ['settings', 'billing', 'financial_periods_enabled'],
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<Record<string, unknown>>>('/settings/billing');
      return response.data.data;
    },
  });

  const isEnabled = billingSettings?.financial_periods_enabled === true;

  const toggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      await apiClient.put('/settings/billing', {
        settings: [{ key: 'financial_periods_enabled', value: enabled }],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'billing'] });
    },
  });

  return (
    <label className="relative inline-flex shrink-0 cursor-pointer items-center">
      <input
        type="checkbox"
        checked={isEnabled}
        onChange={(e) => toggleMutation.mutate(e.target.checked)}
        disabled={toggleMutation.isPending}
        className="peer sr-only"
        aria-label="Toggle financial period enforcement"
      />
      <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-2 peer-focus:ring-brand-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-brand-800" />
    </label>
  );
}

// (Payment reminder scheduling is configured per practice entity in the
// Billing Entity Configuration screen, not at the tenant level.)
